/**
 * Gemeinsame CSV-Hilfsfunktionen für alle Exporte.
 *
 * Kontrakt für sämtliche CSV-Downloads der App:
 *   * UTF-8 mit BOM (Excel-DE öffnet Umlaute korrekt).
 *   * Semikolon als Trennzeichen (deutsches Locale, kein "Text-in-Spalten"-Assistent).
 *   * Metadatenblock oberhalb der Tabelle (Titel, Bereich, Datum, aktive Filter).
 *   * CRLF-Zeilenenden (RFC-4180).
 *
 * Bewusst schlank gehalten — keine Bibliotheks-Abhängigkeit, kein
 * Streaming, kein Column-Type-System. Alle Werte werden als String
 * erwartet oder von den Aufrufern selbst formatiert (z. B. deutsches
 * Dezimalkomma via ``"0,40"`` statt Number).
 */

/** RFC-4180-konformes Escaping. */
export function csvEscape(v) {
  const s = v === null || v === undefined ? "" : String(v);
  if (/[";\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

/** Baut eine einzelne CSV-Zeile aus Werten. */
export function toCsvRow(cols) {
  return cols.map(csvEscape).join(";");
}

/** DD.MM.YYYY HH:mm im deutschen Format (Local Time). */
export function formatGermanDateTime(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** YYYY-MM-DD im ISO-Format (für Dateinamen). */
export function isoDate(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Slug für Filenamen (Umlaute + Sonderzeichen entfernt). */
export function slugForFilename(s) {
  if (!s) return "";
  return String(s)
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Baut den Metadatenblock nach vereinbartem Schema:
 *
 *   Zeile 1: Loop Inventory
 *   Zeile 2: <area>                       (z. B. "Produkte" / "Lagerbestand")
 *   Zeile 3: (leer)
 *   Zeile 4: Datum: DD.MM.YYYY HH:mm
 *   Zeile 5: Filter:
 *   Zeile 6..n:   <Label>: <Wert>         (mit führenden 2 Spaces)
 *   (leere Zeile am Ende)
 *
 * ``filters`` = Array von ``{ label, value }``. Werte werden 1:1
 * übernommen — Aufrufer entscheidet, ob "Alle" oder ein Ist-Wert
 * geschrieben wird.
 */
export function buildMetaLines({ area, filters = [], date = new Date() }) {
  const lines = [];
  lines.push(toCsvRow(["Loop Inventory"]));
  lines.push(toCsvRow([area]));
  lines.push("");
  lines.push(toCsvRow([`Datum: ${formatGermanDateTime(date)}`]));
  lines.push(toCsvRow(["Filter:"]));
  for (const f of filters) {
    // Zwei Leerzeichen als Einrückung — bleibt in Excel als Text-Präfix
    // erhalten, weil das ganze Feld eine einzelne Zelle ist.
    lines.push(toCsvRow([`  ${f.label}: ${f.value}`]));
  }
  lines.push(""); // Leerzeile zur Trennung zum Tabellenkopf
  return lines;
}

/** Fügt Meta + Header + Datenzeilen zu einem vollständigen CSV-Text zusammen. */
export function assembleCsv({ metaLines, headers, rows }) {
  const parts = [];
  if (metaLines?.length) parts.push(...metaLines);
  parts.push(toCsvRow(headers));
  for (const r of rows) parts.push(toCsvRow(r));
  return parts.join("\r\n");
}

/** Startet den Browser-Download für die zusammengebaute CSV. */
export function downloadCsv(filename, csvText) {
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Deutsches Dezimalformat für Beträge (z. B. Preise). */
export function germanNumber(n, digits = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return v.toFixed(digits).replace(".", ",");
}
